import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Loader2, Mail, ChevronRight, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HeroSection from "@/components/HeroSection";
import SEOIntroSection from "@/components/SEOIntroSection";
import CookieConsent from "@/components/CookieConsent";
import SEOHead from "@/components/SEOHead";
import ScrollToTop from "@/components/ScrollToTop";
import { CategoryCardStack } from "@/components/CategoryCardStack";
import { ArticleCard } from "@/components/ArticleCard";
import SabbatTopicsList from "@/components/SabbatTopicsList";
import PopularArticlesSection from "@/components/PopularArticlesSection";
import { Logos3Demo } from "@/components/blocks/logos3-demo";
import { useCategories, useArticles } from "@/hooks/useArticles";
import airportSilhouette from "@/assets/images/airport-silhouette.webp";

const Index = () => {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Categories and articles
  const { data: categories, isLoading: categoriesLoading } = useCategories();
  const { data: articles, isLoading: articlesLoading } = useArticles();

  // Get latest articles (first 6)
  const latestArticles = articles?.slice(0, 6) || [];
  const featuredArticle = latestArticles[0];
  const otherArticles = latestArticles.slice(1, 4);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsLoading(true);
    try {
      const { data, error: rpcError } = await supabase.rpc("insert_email_signup", {
        signup_email: email
      });
      if (rpcError) {
        throw rpcError;
      }
      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        if (result.error === "Email already registered") {
          toast({
            title: "Allerede tilmeldt",
            description: "Denne e-mail er allerede tilmeldt.",
            variant: "destructive"
          });
        } else if (result.error === "Too many signups, please try again later") {
          toast({
            title: "For mange forsøg",
            description: "Prøv venligst igen om et øjeblik.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Ugyldig e-mail",
            description: "Indtast venligst en gyldig e-mailadresse.",
            variant: "destructive"
          });
        }
        setIsLoading(false);
        return;
      }
      const { error: emailError } = await supabase.functions.invoke("send-confirmation-email", {
        body: { email }
      });
      if (emailError) {
        console.error("Error sending email:", emailError);
      }
      setIsSubmitted(true);
    } catch (error: any) {
      console.error("Error during signup:", error);
      toast({
        title: "Der opstod en fejl",
        description: "Prøv venligst igen senere.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEOHead
        title="MySabbat – Guides til sabbatår, rejse, job og højskole"
        description="Planlægger du sabbatår? Få gratis guides til rejse, job, højskole og økonomi. MySabbat hjælper unge med at træffe det rigtige valg. Start her!"
        path=""
        keywords={["sabbatår", "sabbatår guide", "sabbatår for unge", "rejse i sabbatår", "gap year", "højskole", "working holiday", "backpacking"]}
      />
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <ScrollToTop />
        
        <main>
          {/* Hero Section */}
          <HeroSection />

          {/* Popular Categories */}
          <section className="py-12 md:py-16 bg-background">
            <div className="container">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                    Populære kategorier
                  </h2>
                  <p className="text-muted-foreground mt-1">
                    Udforsk emner der interesserer dig
                  </p>
                </div>
              </div>
              
              {categoriesLoading ? (
                <div className="flex gap-4 overflow-hidden">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-40 md:w-48 aspect-[3/4] bg-muted rounded-2xl animate-pulse shrink-0" />
                  ))}
                </div>
              ) : categories && categories.length > 0 ? (
                <CategoryCardStack categories={categories} />
              ) : null}
            </div>
          </section>

          {/* Udforsk emner */}
          <SabbatTopicsList />

          {/* SEO Intro Section */}
          <SEOIntroSection />

          {/* Partner Logos */}
          <Logos3Demo />



          {/* Popular Articles */}
          <PopularArticlesSection />

          {/* Latest Articles */}
          <section className="py-12 md:py-16 bg-muted/50">
            <div className="container">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                    Seneste artikler
                  </h2>
                  <p className="text-muted-foreground mt-1">
                    Guides og inspiration til dit sabbatår
                  </p>
                </div>
                <Link 
                  to="/kategori/sabbataar-intro"
                  className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                >
                  Se alle
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {articlesLoading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className={`bg-card rounded-2xl animate-pulse ${i === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}>
                      <div className="aspect-[16/10] bg-muted rounded-t-2xl" />
                      <div className="p-5 space-y-3">
                        <div className="h-5 w-20 bg-muted rounded-full" />
                        <div className="h-6 w-3/4 bg-muted rounded" />
                        <div className="h-4 w-full bg-muted rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {featuredArticle && (
                    <div className="md:col-span-2 lg:col-span-1 lg:row-span-2">
                      <ArticleCard article={featuredArticle} featured colorAccent="coral" />
                    </div>
                  )}
                  {otherArticles.map((article, index) => (
                    <ArticleCard 
                      key={article.id} 
                      article={article} 
                      colorAccent={["marine", "sage", "sky"][index % 3]}
                    />
                  ))}
                </div>
              )}

              <div className="mt-8 text-center sm:hidden">
                <Link 
                  to="/kategori/sabbataar-intro"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                >
                  Se alle artikler
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 md:py-20 relative overflow-hidden">
            {/* Background image */}
            <div className="absolute inset-0">
              <img 
                src={airportSilhouette}
                alt="Rejsende i lufthavn"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-primary/85" />
            </div>
            
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent/10 rounded-full blur-3xl" />
            
            <div className="container relative">
              <div className="max-w-lg mx-auto text-center">
                <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-3">
                  Få overblik over dit sabbatår
                </h2>
                
                <p className="text-primary-foreground/80 mb-8">
                  Tilmeld dig og få besked, når vores app er klar
                </p>

                {!isSubmitted ? (
                  <div className="bg-card rounded-2xl p-6 shadow-xl">
                    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                      <div className="relative flex-1">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input 
                          type="email" 
                          placeholder="Din e-mail" 
                          value={email} 
                          onChange={e => setEmail(e.target.value)} 
                          required 
                          className="pl-10 h-12 rounded-xl border-border" 
                          disabled={isLoading} 
                        />
                      </div>
                      <Button 
                        type="submit" 
                        size="lg" 
                        disabled={isLoading} 
                        className="h-12 px-8 rounded-xl bg-accent text-accent-foreground font-semibold hover:bg-accent/90"
                      >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tilmeld"}
                      </Button>
                    </form>
                    <p className="text-xs text-muted-foreground mt-4">
                      Ingen spam. Kun en besked når appen er klar.
                    </p>
                  </div>
                ) : (
                  <div className="bg-card rounded-2xl p-8 shadow-xl">
                    <div className="w-14 h-14 rounded-full bg-ms-sage flex items-center justify-center mx-auto mb-4">
                      <Check className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">
                      Du er på listen!
                    </h3>
                    <p className="text-muted-foreground">
                      Vi giver besked, når appen er klar.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <CookieConsent />
      </div>
    </>
  );
};

export default Index;
